// src/pages/orders/Order-details.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resolveOrderGid } from '../../actions/order_edit_actions';
import { EditToggle, AddressEditor, CustomerEditor, NoteEditor, FulfillmentActions } from './orderEditPanels';
import { Card, CardHeader, CardBody, CardFooter } from '../../components/card/card.jsx';
import { fetchOrderDetails, fetchChannelOrderDetails } from '../../actions/orders_actions';
import { fetchShakerproOrderDetails } from '../../actions/shakerpro_orders_actions';
import { fetchCustomerJourney } from '../../actions/customer_actions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

function PagesOrderDetails() {
	// const { orderId } = useParams();
	// 2. Setup Redux
  	const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const backHref = `/orders${location.search || ''}`; // falls back if no history


    const { channel: channelParam, orderId } = useParams();
    const channelNorm = (channelParam || 'us').toLowerCase();
    const isShakerpro = channelNorm === 'shakerpro';
    const isChannel = channelNorm === 'uk' || channelNorm === 'india' || channelNorm === 'saleor-us';

    const lastKeyRef = useRef('');
    useEffect(() => {
        if (!orderId) return;
        const key = `${isShakerpro ? 'shakerpro' : isChannel ? 'saleor' : 'us'}|${orderId}`;
        if (lastKeyRef.current === key) return;
        lastKeyRef.current = key;
        if (isShakerpro)   dispatch(fetchShakerproOrderDetails(orderId));
        else if (isChannel) dispatch(fetchChannelOrderDetails(orderId, channelNorm === 'uk' ? 'GBP' : channelNorm === 'india' ? 'INR' : null));
        else               dispatch(fetchOrderDetails(orderId));
    }, [dispatch, orderId, isChannel, isShakerpro]);

    const symbolFromChannel = ch =>
        ch === 'uk' ? '£' : ch === 'india' ? '₹' : '$'; // shakerpro is USD

	// Get order details from redux
    const { details, loading, error } = useSelector(state => ({
        details : isShakerpro ? state.orders.shakerpro_order_details         : isChannel ? state.orders.channel_order_details         : state.orders.order_details,
        loading : isShakerpro ? state.orders.loading_shakerpro_order_details : isChannel ? state.orders.loading_channel_order_details : state.orders.loading_order_details,
        error   : isShakerpro ? state.orders.shakerpro_order_details_error   : isChannel ? state.orders.channel_order_details_error   : state.orders.order_details_error,
    }));

    // Which card is open for editing, and the engine id every mutation needs.
    //
    // The page is keyed on the order NUMBER; the engine's admin mutations take a
    // gid. Resolved once when details land. A null gid is a real state, not an
    // error: legacy US orders live only in the analytics database and were never
    // mirrored, so nothing here can edit them — the panels say so rather than
    // failing on save.
    const [editing, setEditing] = useState(null);
    const [orderGid, setOrderGid] = useState(null);
    const [engineStatus, setEngineStatus] = useState(null);
    const [engineFulfillments, setEngineFulfillments] = useState([]);
    const gidForRef = useRef('');

    useEffect(() => {
        // ShakePro orders are never in the commerce engine — skip the lookup.
        if (isShakerpro) return;
        const num = details?.orderId;
        if (!num || gidForRef.current === String(num)) return;
        gidForRef.current = String(num);
        setOrderGid(null);
        setEngineFulfillments([]);
        resolveOrderGid(num).then((node) => {
            setOrderGid(node ? node.id : null);
            setEngineStatus(node ? node.status : null);
            setEngineFulfillments(node ? (node.fulfillments || []) : []);
        });
    }, [details, isShakerpro]);

    // Re-read fulfillment state directly from the engine after a successful
    // fulfil+track action. The analytics API doesn't know about engine-native
    // tracking data, so refetch() (which hits the analytics API) would always
    // return empty tracking. This hits the engine directly and updates the display.
    const refreshEngine = () => {
        if (isShakerpro) return;
        const num = details?.orderId;
        if (!num) return;
        resolveOrderGid(num).then((node) => {
            setEngineStatus(node ? node.status : null);
            setEngineFulfillments(node ? (node.fulfillments || []) : []);
        });
    };

    // After a successful edit, close the form and refetch through whichever source
    // this page is using — the panels never touch redux themselves.
    const refetch = () => {
        setEditing(null);
        const id = details?.orderId;
        if (!id) return;
        gidForRef.current = '';
        if (isShakerpro)   dispatch(fetchShakerproOrderDetails(id));
        else if (isChannel) dispatch(fetchChannelOrderDetails(id));
        else               dispatch(fetchOrderDetails(id));
    };

    const fulfilled = String(details?.fulfillmentStatus || '').toLowerCase() === 'fulfilled';

    // Customer journey (Braze) — fetched once per email when details load
    const {
        customer_journey,
        loading_customer_journey,
        customer_journey_error,
    } = useSelector(state => state.orders);

    const lastJourneyEmailRef = useRef('');
    useEffect(() => {
        const email = details?.email;
        if (!email || !String(email).includes('@')) return;
        if (lastJourneyEmailRef.current === email) return;
        lastJourneyEmailRef.current = email;
        dispatch(fetchCustomerJourney(email));
    }, [dispatch, details]);

    const fmtWhen = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
    };

    const optInBadge = (label, value) => {
        const v = String(value || 'unknown').toLowerCase();
        const cls = v === 'opted_in' || v === 'subscribed'
            ? 'border-success text-success'
            : v === 'unsubscribed'
            ? 'border-danger text-danger'
            : 'border-gray-500 text-gray-500';
        return (
            <span key={label} className={`badge border px-2 pt-5px pb-5px rounded fs-12px me-2 ${cls}`}>
                {label}: {v.replace(/_/g, ' ')}
            </span>
        );
    };

  // 3. Fetch order details on mount
//   useEffect(() => {
//     if (orderId) {
//       dispatch(fetchOrderDetails(orderId));
//     }
//   }, [dispatch, orderId]);

  // Currency symbols by ISO code. The RECORD's currency wins over the channel:
  // 'shakerpro' is not a currency, and assuming USD rendered a ₹1,999 India order
  // as $1,999 (and its USD-list subtotal as $2,199.56).
  const SYMBOL_BY_CURRENCY = { USD: '$', INR: '₹', GBP: '£', EUR: '€' };

  // Helper to format currency
  const fmt = v => {
    // details.currency is derived server-side from the order's paymentMethod — the
    // feed sends no currency field at all. The channel guess is only a fallback for
    // records that do not state one; it never overrides the record.
    const sym =
        SYMBOL_BY_CURRENCY[String(details?.currency || '').toUpperCase()] ||
        symbolFromChannel(channelNorm);
    return typeof v === 'number'
        ? `${sym}${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
        : v;
    };

  const getGoogleMapsUrl = (shipping) => {
  if (!shipping) return "#/";

  const addressParts = [
    shipping.line1,
    shipping.line2,
    shipping.city,
    shipping.state,
    shipping.zip,
    shipping.country,
  ]
    .filter(Boolean)
    .join(", ");

  return `https://www.google.com/maps/search/?q=${encodeURIComponent(addressParts)}`;
};

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">PAGES</a></li>
            <li className="breadcrumb-item active">ORDER DETAILS</li>
          </ol>
          <h1 className="page-header mb-0">
            ORDER DETAILS
            {details?.number ? <span> — {details.number}</span> : null}
          </h1>
          {details?.number ? (
            <div className="small text-inverse text-opacity-50">
              {/* Label by PROVIDER, not channel. order_TO3lctzoaLX4sq is a Razorpay
                  order id; calling it a PayPal ID sends whoever is reconciling to the
                  wrong dashboard. paymentProvider is resolved server-side from
                  paymentMethod, because the feed sends no payment_provider field. */}
              {isShakerpro
                ? `${details.paymentProvider === 'razorpay' ? 'Razorpay Order ID' : 'PayPal ID'}: ${details.orderId}`
                : `Order ID: ${details.orderId}`}
            </div>
          ) : null}
          {details?.time ? (
            <div className="small text-inverse text-opacity-50 mt-1">
              {fmtWhen(details.time)}
            </div>
          ) : null}
        </div>
      </div>

      {/* (1) Top actions unchanged */}
      <div className="mb-3 d-md-flex fw-bold">
        {/* ... no change */}
      </div>

      <div className="row gx-4">
        <div className="col-lg-8">
          {/* Products */}
          <Card className="mb-4">
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Products {details ? `(${details.items?.length || 0})` : '(3)'}
              {/* <a href="#/" className="ms-auto text-decoration-none text-inverse text-opacity-50"><i className="bi bi-truck fa-lg me-1"></i> Add Tracking Link</a> */}
            </CardHeader>
            <CardBody className="text-inverse">
              {loading ? (
                <div className="text-center py-5">
                  <span className="spinner-border"></span>
                </div>
              ) : details && details.items ? (
                <>
                  {details.items.map((item, idx) => (
                    <React.Fragment key={item.upc || idx}>
                      <div className="row align-items-center">
                        <div className="col-lg-8 d-flex align-items-center">
                          <div className="h-65px w-65px d-flex align-items-center justify-content-center position-relative bg-white p-1">
                            <img src={item.image || "/assets/img/glassshimmer.png"} alt="" className="mw-100 mh-100" />
                            <span className="w-20px h-20px p-0 d-flex align-items-center justify-content-center badge bg-theme text-theme-color position-absolute end-0 top-0 fw-bold fs-12px rounded-pill mt-n2 me-n2">{item.quantity}</span>
                          </div>
                          <div className="ps-3 flex-1">
                            <div><a href="#/" className="text-decoration-none text-inverse">{item.title}</a></div>
                            <div className="text-inverse text-opacity-50 small">
                              SKU: {item.upc || ''}
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-2 m-0 ps-lg-3 text-end">
                          <div className="fw-bold">
                            {typeof item.unitPrice === 'number'
                              ? `${fmt(item.unitPrice)} x ${item.quantity}`
                              : `x ${item.quantity}`}
                          </div>

                          {typeof item.unitTax === 'number' && (
                            <div className="small text-inverse text-opacity-50">
                              Tax: {fmt(item.unitTax)} / unit 
                            </div>
                          )}
                        </div>
                        <div className="col-lg-2 m-0 text-end">
                          {/* Item price * quantity, if available */}
                        </div>
                      </div>
                      {idx < details.items.length - 1 && <hr className="my-4 opacity-2" />}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                // If no redux data, keep static fallback
                <>
                  {/* ...your static content as before */}
                </>
              )}
            </CardBody>
            {/* <CardFooter className="bg-none d-flex p-3">
              <a href="#/" className="btn btn-outline-default ms-auto dropdown-toggle">More</a>
              <a href="#/" className="btn btn-outline-theme ms-2">Add Tracking</a>
            </CardFooter> */}
          </Card>

          {/* Payment Records */}
          <Card>
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Payment Records
              {/* <a href="#/" className="ms-auto text-decoration-none text-inverse text-opacity-50"><i className="bi bi-paypal me-1 fa-lg"></i> View paypal records</a> */}
            </CardHeader>
            <CardBody>
              <table className="table table-borderless table-sm m-0">
                <tbody>
                  <tr>
                    <td className="w-150px">Subtotal</td>
                    <td>{details ? `${details.items?.length || 0} items` : (loading ? '…' : '—')}</td>
                    <td className="text-end">{details ? fmt(details.financials?.subtotal) : (loading ? '…' : '—')}</td>
                  </tr>
                  <tr>
                    <td>Tax</td>
                    <td></td>
                    <td className="text-end">{details ? fmt(details.financials?.tax) : (loading ? '…' : '—')}</td>
                  </tr>
                  <tr>
                    <td>Shipping Fee</td>
                    <td>
                      {details?.financials?.discountCode ? (
                        <>promo code: <u className="text-success fw-bold small">{details.financials.discountCode}</u></>
                      ) : ''}
                      {/* <small>(<span className="text-decoration-line-through">$120.00</span>)</small> */}
                    </td>
                    <td className="text-end">{details ? fmt(details.financials?.shipping_fee) : '$0.00'}</td>
                  </tr>
                  <tr>
                    <td className="pb-2" colSpan="2"><b>Total</b></td>
                    <td className="text-end pb-2 text-decoration-underline"><b>{details ? fmt(details.financials?.total) : (loading ? '…' : '—')}</b></td>
                  </tr>
                  <tr>
                    <td colSpan="3"><hr className="m-0" /></td>
                  </tr>
                  <tr>
                    <td className="pt-2 pb-2 text-nowrap">
                      Paid by customer
                    </td>
                    <td className="pt-2 pb-2">
                      {details?.platform && details.platform !== "0" ? (
						<>via <a href="#/" className="text-decoration-none">{details.platform}</a></>
						) : (
						"via Debit or Credit Card"
						)}
                      {details?.paymentMethod ? (
                        <span className="text-inverse text-opacity-50 text-capitalize">
                          {" "}· {String(details.paymentMethod).replace(/_/g, " ")}
                        </span>
                      ) : null}
                    </td>
                    <td className="pt-2 pb-2 text-end">{details ? fmt(details.financials?.total) : (loading ? '…' : '—')}</td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
			{/* <CardFooter className="bg-none d-flex p-3">
              <a href="#/" className="btn btn-outline-theme ms-auto">Mark as paid</a>
            </CardFooter> */}
          </Card>
        </div>

        {/* Right Column */}
        <div className="col-lg-4">
          {/* Notes */}
          <Card className="mb-4">
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Notes
              <EditToggle editing={editing === 'note'} onClick={() => setEditing(editing === 'note' ? null : 'note')}
                disabled={!orderGid} disabledReason={isShakerpro ? 'ShakePro orders cannot be edited from this dashboard' : 'Legacy order — editing is not available'} />
            </CardHeader>
            <CardBody>
              {editing === 'note' ? (
                <NoteEditor gid={orderGid} note={details?.orderNote} onSaved={refetch} onCancel={() => setEditing(null)} />
              ) : (details?.orderNote ? details.orderNote : "No notes from customer")}
            </CardBody>
          </Card>

          {/* Customer */}
          <Card className="mb-4">
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Customer
              <EditToggle editing={editing === 'customer'} onClick={() => setEditing(editing === 'customer' ? null : 'customer')}
                disabled={!orderGid} disabledReason={isShakerpro ? 'ShakePro orders cannot be edited from this dashboard' : 'Legacy order — editing is not available'} />
            </CardHeader>
            <CardBody className="fw-bold">
              {editing === 'customer' ? (
                <CustomerEditor gid={orderGid} name={details?.customer} email={details?.email}
                  onSaved={refetch} onCancel={() => setEditing(null)} />
              ) : (
              <div className="d-flex align-items-center">
                <a href="#/" className="d-block">
                  <img
                    src="/assets/img/usershimmer.png"
                    alt=""
                    width="45"
                    className="rounded-pill"
                  />
                </a>
                <div className="flex-1 ps-3">
                  <a href="#/" className="d-block text-decoration-none">
                    {details?.customer || <span className="text-inverse text-opacity-50">Name not provided</span>}
                  </a>
                  {details?.email || <span className="text-inverse text-opacity-50">No email on file</span>}
                </div>
              </div>
              )}
            </CardBody>
          </Card>

          {/* Shipping Information */}
          <Card className="mb-4">
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Shipping Information
              <EditToggle editing={editing === 'shipping'} onClick={() => setEditing(editing === 'shipping' ? null : 'shipping')}
                disabled={!orderGid || fulfilled}
                disabledReason={fulfilled
                  ? 'This order has been fulfilled — the label and the PayPal tracker carry the old address'
                  : isShakerpro ? 'ShakePro orders cannot be edited from this dashboard' : 'Legacy order — editing is not available'} />
            </CardHeader>
            <CardBody className="fw-bold">
              {editing === 'shipping' ? (
                <AddressEditor gid={orderGid} which="shipping" current={details?.shipping}
                  onSaved={refetch} onCancel={() => setEditing(null)} />
              ) : (<>
              {details?.phone && (
                <>
                  <i className="bi bi-telephone-fill fa-fw"></i> {details.phone}
                  <br /><br />
                </>
              )}
              {details?.shipping ? (
                <>
                  {details.shipping.line1 && <>{details.shipping.line1}<br /></>}
                  {details.shipping.line2 && <>{details.shipping.line2}<br /></>}
                  {details.shipping.city && <>{details.shipping.city}<br /></>}
                  {details.shipping.state && <>{details.shipping.state}<br /></>}
                  {details.shipping.zip && <>{details.shipping.zip}<br /></>}
                  {details.shipping.country && <>{details.shipping.country}<br /></>}
                </>
              ) : (
                <span className="text-inverse text-opacity-50">Not available</span>
              )}
              <br />
			<a
				href={getGoogleMapsUrl(details?.shipping)} 
				className="text-decoration-none" 
				target="_blank" 
				rel="noopener noreferrer"
			>
				<i className="bi bi-geo-alt-fill fa-fw"></i> View map
			</a>
              </>)}
            </CardBody>
          </Card>

          {/* Billing Information */}
          <Card className="mb-4">
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Billing Information
              <EditToggle editing={editing === 'billing'} onClick={() => setEditing(editing === 'billing' ? null : 'billing')}
                disabled={!orderGid || fulfilled}
                disabledReason={fulfilled
                  ? 'This order has been fulfilled — the invoice already carries the old address'
                  : isShakerpro ? 'ShakePro orders cannot be edited from this dashboard' : 'Legacy order — editing is not available'} />
            </CardHeader>
            <CardBody className="fw-bold">
              {editing === 'billing' ? (
                <AddressEditor gid={orderGid} which="billing" current={details?.billing}
                  onSaved={refetch} onCancel={() => setEditing(null)} />
              ) : details?.billing ? (
                <>
                  {details.billing.line1 && <>{details.billing.line1}<br /></>}
                  {details.billing.line2 && <>{details.billing.line2}<br /></>}
                  {details.billing.city && <>{details.billing.city}<br /></>}
                  {details.billing.state && <>{details.billing.state}<br /></>}
                  {details.billing.zip && <>{details.billing.zip}<br /></>}
                </>
              ) : (
                <span className="text-inverse text-opacity-50">Not available</span>
              )}
            </CardBody>
          </Card>

          {/* Fulfillment */}
          {(details?.fulfillmentStatus || details?.expectedShipDate || orderGid) && (
            <Card className="mb-4">
              <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
                Fulfillment
              </CardHeader>
              <CardBody>
                {(() => {
                  // If the engine reports FULFILLED rows but analytics API hasn't
                  // caught up yet (freshly fulfilled engine-native orders), show
                  // Fulfilled rather than the stale analytics value.
                  const engineFulfilled = engineFulfillments.some(f => f.status === 'FULFILLED');
                  const displayStatus = engineFulfilled ? "Fulfilled" : (details?.fulfillmentStatus || "Unfulfilled");
                  return (
                    <span
                      className={
                        "badge border px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center " +
                        (displayStatus === "Fulfilled"
                          ? "border-success text-success"
                          : "border-warning text-warning")
                      }
                    >
                      <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                      {displayStatus}
                    </span>
                  );
                })()}
                {(() => {
                  // Prefer engine fulfillment data (engine-native orders); fall
                  // back to analytics API data (mirrored/legacy orders).
                  const ef = engineFulfillments.find(f => f.trackingNumber);
                  const trackingNum = ef ? ef.trackingNumber : details?.tracking;
                  const trackingCarrier = ef ? ef.carrier : details?.carrier;
                  // Count as fulfilled if analytics says so OR the engine has a
                  // FULFILLED fulfillment row (catches freshly-fulfilled orders
                  // before the analytics API has been re-synced).
                  const isFulfilled = details?.fulfillmentStatus === "Fulfilled" ||
                    engineFulfillments.some(f => f.status === 'FULFILLED');
                  return isFulfilled && trackingNum ? (
                    <div className="mt-2 small">
                      <span className="text-inverse text-opacity-50">Tracking:</span>{" "}
                      {trackingCarrier ? `${trackingCarrier} · ` : ""}{trackingNum}
                    </div>
                  ) : null;
                })()}
                {details?.fulfillmentStatus !== "Fulfilled" && details?.expectedShipDate ? (
                  <div className="mt-2 small">
                    <span className="text-inverse text-opacity-50">Expected to ship:</span>{" "}
                    <b>{details.expectedShipDate}</b>
                  </div>
                ) : null}
                {(() => {
                  const ef = engineFulfillments.find(f => f.trackingNumber);
                  return (
                    <FulfillmentActions gid={orderGid} status={engineStatus}
                      tracking={ef ? ef.trackingNumber : details?.tracking}
                      carrier={ef ? ef.carrier : details?.carrier}
                      onDone={refreshEngine} />
                  );
                })()}
              </CardBody>
            </Card>
          )}

          {details && details.journey ? (<Card className="mb-4"><CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">Collins Journey<span className="ms-auto text-inverse text-opacity-50 small">ai.barsys.com</span></CardHeader><CardBody><div className="small mb-2">{'Prompts: ' + ((details.journey.counts || {}).prompts || 0) + ' · Products viewed: ' + ((details.journey.counts || {}).products_viewed || 0) + ' · Session: ' + Math.round((details.journey.session_seconds || 0) / 60) + ' min'}</div><div className="small text-inverse text-opacity-50 mb-2">{(details.journey.referrer ? 'Referrer: ' + details.journey.referrer : 'Direct visit') + (details.journey.utm && Object.keys(details.journey.utm).length ? ' · ' + Object.entries(details.journey.utm).map(function (p) { return p[0] + '=' + p[1]; }).join(' · ') : '')}</div><div style={{ maxHeight: 260, overflowY: 'auto' }}>{(details.journey.events || []).map(function (ev, i) { return (<div key={i} className="d-flex small py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}><span className="text-inverse text-opacity-50" style={{ width: 56, flexShrink: 0 }}>{Math.floor((ev.t || 0) / 60) + ':' + String((ev.t || 0) % 60).padStart(2, '0')}</span><span>{String(ev.e || '').replace('barbot_', '').split('_').join(' ') + (ev.d ? ' — ' + Object.values(ev.d).join(', ') : '')}</span></div>); })}</div></CardBody></Card>) : null}{/* Customer Journey (Braze) */}
          <Card className="mb-4">
            <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
              Customer Journey
              <span className="ms-auto text-inverse text-opacity-50 small">Braze</span>
            </CardHeader>
            <CardBody>
              {loading_customer_journey ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm" role="status"></div>
                </div>
              ) : customer_journey_error ? (
                <span className="text-inverse text-opacity-50 small">{customer_journey_error}</span>
              ) : !customer_journey ? (
                <span className="text-inverse text-opacity-50 small">
                  {details?.email ? 'No journey data' : 'No email on this order yet'}
                </span>
              ) : (
                <>
                  {/* Opt-ins */}
                  <div className="mb-3">
                    <div className="fw-bold small text-inverse text-opacity-50 mb-1">MARKETING OPT-INS</div>
                    {optInBadge('Email', customer_journey.optIns?.email)}
                    {optInBadge('Push', customer_journey.optIns?.push)}
                    {(customer_journey.optIns?.subscriptionGroups || []).map((g) =>
                      optInBadge(`${g.channel || 'group'} · ${g.name}`, g.status)
                    )}
                  </div>

                  {/* Acquisition / attribution */}
                  <div className="mb-3">
                    <div className="fw-bold small text-inverse text-opacity-50 mb-1">ACQUISITION</div>
                    {customer_journey.attribution && Object.keys(customer_journey.attribution).length ? (
                      <table className="table table-sm mb-0 small">
                        <tbody>
                          {Object.entries(customer_journey.attribution).map(([k, v]) => (
                            <tr key={k}>
                              <td className="text-inverse text-opacity-50">{k}</td>
                              <td className="text-break">{v == null ? '—' : String(v)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <span className="text-inverse text-opacity-50 small">No attribution attributes on profile</span>
                    )}
                    {customer_journey.profile?.createdAt && (
                      <div className="small text-inverse text-opacity-50 mt-1">
                        Profile created {fmtWhen(customer_journey.profile.createdAt)}
                        {customer_journey.profile?.totalRevenue != null &&
                          <> · lifetime ${customer_journey.profile.totalRevenue}</>}
                      </div>
                    )}
                  </div>

                  {/* Messages received */}
                  <div className="mb-2">
                    <div className="fw-bold small text-inverse text-opacity-50 mb-1">
                      MESSAGES RECEIVED ({(customer_journey.messages || []).length})
                    </div>
                    {(customer_journey.messages || []).length ? (
                      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {(customer_journey.messages || []).map((m, i) => (
                          <div key={i} className="d-flex align-items-start py-1 border-bottom border-inverse border-opacity-10">
                            <i className={`bi ${m.type === 'canvas' ? 'bi-diagram-3' : 'bi-envelope'} me-2 mt-1`}></i>
                            <div className="flex-1 small">
                              <div className="fw-bold">{m.name}</div>
                              <div className="text-inverse text-opacity-50">
                                {m.type}{m.lastReceived ? ` · ${fmtWhen(m.lastReceived)}` : ''}
                                {m.engaged?.opened_email ? ' · opened' : ''}
                                {m.engaged?.clicked_email ? ' · clicked' : ''}
                              </div>
                              {(m.stepsReceived || []).length ? (
                                <div className="text-inverse text-opacity-50">
                                  steps: {m.stepsReceived.map((s) => s.name).join(', ')}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-inverse text-opacity-50 small">No campaigns or canvases received yet</span>
                    )}
                  </div>

                  {/* Devices */}
                  {(customer_journey.devices || []).length ? (
                    <div>
                      <div className="fw-bold small text-inverse text-opacity-50 mb-1">DEVICES</div>
                      <div className="small text-inverse text-opacity-75">
                        {customer_journey.devices
                          .map((d) => [d.model, d.os, d.browser].filter(Boolean).join(' / '))
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default PagesOrderDetails;
